import { useState, useEffect } from "react";
import PhaseView from "./components/PhaseView";
import Progress from "./components/Progress";
import Compare from "./components/Compare";
import HrZones from "./components/HrZones";
import Wiki from "./components/Wiki";
import Login from "./components/Login";
import phase1 from "./data/phase1";
import phase2 from "./data/phase2";
import phase3 from "./data/phase3";
import phase4 from "./data/phase4";

const PHASES = [
  { key: 1, label: "Phase 1", sub: "Foundation", weeks: "Wks 1–4", color: "#E8650A", data: phase1 },
  { key: 2, label: "Phase 2", sub: "Endurance", weeks: "Wks 5–10", color: "#2E75B6", data: phase2 },
  {
    key: 3,
    label: "Phase 3",
    sub: "Race Specific",
    weeks: "Wks 11–16",
    color: "#27AE60",
    data: phase3,
  },
  {
    key: 4,
    label: "Phase 4",
    sub: "Taper & Race",
    weeks: "Wks 17–19",
    color: "#C0392B",
    data: phase4,
  },
];

export default function App() {
  // Restore nav state from localStorage on mount
  const [activePhase, setActivePhase] = useState(() => {
    return parseInt(localStorage.getItem("nav_phase") || "1", 10);
  });
  const [showProgress, setShowProgress] = useState(() => {
    const v = localStorage.getItem("nav_view");
    return v === "progress" || v === "compare";
  });
  const [showCompare, setShowCompare] = useState(() => {
    return localStorage.getItem("nav_view") === "compare";
  });

  const [stravaId, setStravaId] = useState(null);
  const [athleteName, setAthleteName] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [allRuns, setAllRuns] = useState([]);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("dark_mode") === "true");
  const [timeFormat, setTimeFormat] = useState(() => localStorage.getItem("time_format") || "duration");
  const [showSettings, setShowSettings] = useState(false);
  const [showHrZones, setShowHrZones] = useState(() => localStorage.getItem("nav_view") === "hrzones");
  const [showWiki, setShowWiki] = useState(() => localStorage.getItem("nav_view") === "wiki");
  const [selectedZoneConfigId, setSelectedZoneConfigId] = useState(() => {
    const v = localStorage.getItem("zone_config_id");
    return v ? parseInt(v, 10) : null;
  });
  const [zoneConfigs, setZoneConfigs] = useState([]);

  const current = PHASES.find(p => p.key === activePhase);

  // Persist nav state to localStorage whenever it changes
  useEffect(() => {
    let view = "phase";
    if (showWiki) view = "wiki";
    else if (showHrZones) view = "hrzones";
    else if (showProgress && showCompare) view = "compare";
    else if (showProgress) view = "progress";
    localStorage.setItem("nav_view", view);
    localStorage.setItem("nav_phase", activePhase);
  }, [activePhase, showProgress, showCompare, showHrZones, showWiki]);

  function selectZoneConfig(id) {
    setSelectedZoneConfigId(id);
    localStorage.setItem("zone_config_id", id);
    // Refresh configs list so Compare picks up any newly created config
    fetch("/api/hr-zones", { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => r.json())
      .then(setZoneConfigs)
      .catch(() => {});
  }

  // Fetch zone configs once after login
  useEffect(() => {
    if (!authToken) return;
    fetch("/api/hr-zones", { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => r.json())
      .then(setZoneConfigs)
      .catch(() => {});
  }, [authToken]);

  // On app load: check for existing valid token
  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      setAuthChecked(true);
      return;
    }
    // Verify token is still valid with the server
    fetch("/api/auth/verify", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        if (res.ok) {
          setAuthToken(token);
        } else {
          // Token expired or invalid — clear it
          localStorage.removeItem("auth_token");
          localStorage.removeItem("auth_email");
        }
      })
      .catch(() => {
        // Server unreachable — still allow access with stored token
        // to avoid locking out on bad network
        setAuthToken(token);
      })
      .finally(() => setAuthChecked(true));
  }, []);

  // Pick up strava_id from URL after OAuth redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("strava_id");
    const name = params.get("name");
    if (id) {
      setStravaId(id);
      setAthleteName(name);
      setShowProgress(true);
      localStorage.setItem("strava_id", id);
      localStorage.setItem("athlete_name", name);
      window.history.replaceState({}, "", "/");
    } else {
      const saved = localStorage.getItem("strava_id");
      const savedName = localStorage.getItem("athlete_name");
      if (saved) {
        setStravaId(saved);
        setAthleteName(savedName);
      }
    }
  }, []);

  function handleLogin(token) {
    setAuthToken(token);
  }

  function handleLogout() {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_email");
    localStorage.removeItem("strava_id");
    localStorage.removeItem("athlete_name");
    localStorage.removeItem("nav_view");
    localStorage.removeItem("nav_phase");
    setAuthToken(null);
    setStravaId(null);
    setAthleteName(null);
    setShowProgress(false);
    setShowCompare(false);
  }

  function setAndSaveTimeFormat(fmt) {
    setTimeFormat(fmt);
    localStorage.setItem("time_format", fmt);
  }

  function toggleDarkMode() {
    setDarkMode(d => {
      const next = !d;
      localStorage.setItem("dark_mode", next);
      return next;
    });
  }

  // Splash screen while checking token
  if (!authChecked) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#0F0F1A",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ fontSize: 32 }}>🏃</div>
      </div>
    );
  }

  // Not logged in — show login screen
  if (!authToken) {
    return <Login onLogin={handleLogin} />;
  }

  // Logged in — show the app
  return (
    <div
      data-theme={darkMode ? "dark" : "light"}
      style={{
        margin: 0,
        padding: 0,
        fontFamily: "'Georgia', serif",
        background: "var(--bg-page)",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: "#1A1A2E",
          borderBottom: "2px solid #2A2A4E",
          display: "flex",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
        }}
      >
        {PHASES.map(p => (
          <button
            key={p.key}
            onClick={() => {
              setActivePhase(p.key);
              setShowProgress(false);
              setShowCompare(false);
              setShowHrZones(false);
              setShowWiki(false);
            }}
            style={{
              flex: "0 0 auto",
              padding: "10px 16px",
              border: "none",
              borderBottom:
                !showProgress && !showHrZones && !showWiki && activePhase === p.key
                  ? `3px solid ${p.color}`
                  : "3px solid transparent",
              background: "transparent",
              color: !showProgress && !showHrZones && !showWiki && activePhase === p.key ? "#fff" : "rgba(255,255,255,0.45)",
              fontFamily: "'Georgia', serif",
              cursor: "pointer",
              textAlign: "center",
              minWidth: 80,
              transition: "all 0.15s",
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: "bold",
                color: !showProgress && !showHrZones && !showWiki && activePhase === p.key ? p.color : "rgba(255,255,255,0.4)",
              }}
            >
              {p.label}
            </div>
            <div style={{ fontSize: 11, marginTop: 1 }}>{p.sub}</div>
            <div style={{ fontSize: 10, opacity: 0.6, marginTop: 1 }}>{p.weeks}</div>
          </button>
        ))}

        {/* Progress tab */}
        <button
          onClick={() => {
            setShowProgress(true);
            setShowCompare(false);
            setShowHrZones(false);
            setShowWiki(false);
          }}
          style={{
            flex: "0 0 auto",
            padding: "10px 16px",
            border: "none",
            borderBottom:
              showProgress && !showCompare && !showWiki ? "3px solid #FC4C02" : "3px solid transparent",
            background: "transparent",
            color: showProgress && !showCompare && !showWiki ? "#fff" : "rgba(255,255,255,0.45)",
            fontFamily: "'Georgia', serif",
            cursor: "pointer",
            textAlign: "center",
            minWidth: 80,
            transition: "all 0.15s",
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: "bold",
              color: showProgress && !showCompare && !showWiki ? "#FC4C02" : "rgba(255,255,255,0.4)",
            }}
          >
            Progress
          </div>
          <div style={{ fontSize: 11, marginTop: 1 }}>{athleteName || "Strava"}</div>
          <div style={{ fontSize: 10, opacity: 0.6, marginTop: 1 }}>🏃</div>
        </button>

        {/* Compare tab */}
        <button
          onClick={() => {
            setShowProgress(true);
            setShowCompare(true);
            setShowHrZones(false);
            setShowWiki(false);
          }}
          style={{
            flex: "0 0 auto",
            padding: "10px 16px",
            border: "none",
            borderBottom: showCompare ? "3px solid #9B59B6" : "3px solid transparent",
            background: "transparent",
            color: showCompare ? "#fff" : "rgba(255,255,255,0.45)",
            fontFamily: "'Georgia', serif",
            cursor: "pointer",
            textAlign: "center",
            minWidth: 80,
            transition: "all 0.15s",
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: "bold",
              color: showCompare ? "#9B59B6" : "rgba(255,255,255,0.4)",
            }}
          >
            Compare
          </div>
          <div style={{ fontSize: 11, marginTop: 1 }}>Analysis</div>
          <div style={{ fontSize: 10, opacity: 0.6, marginTop: 1 }}>📊</div>
        </button>

        {/* HR Zones tab */}
        <button
          onClick={() => {
            setShowHrZones(true);
            setShowProgress(false);
            setShowCompare(false);
            setShowWiki(false);
          }}
          style={{
            flex: "0 0 auto",
            padding: "10px 16px",
            border: "none",
            borderBottom: showHrZones ? "3px solid #C0392B" : "3px solid transparent",
            background: "transparent",
            color: showHrZones ? "#fff" : "rgba(255,255,255,0.45)",
            fontFamily: "'Georgia', serif",
            cursor: "pointer",
            textAlign: "center",
            minWidth: 80,
            transition: "all 0.15s",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: "bold", color: showHrZones ? "#C0392B" : "rgba(255,255,255,0.4)" }}>
            HR Zones
          </div>
          <div style={{ fontSize: 11, marginTop: 1 }}>Configure</div>
          <div style={{ fontSize: 10, opacity: 0.6, marginTop: 1 }}>❤</div>
        </button>

        {/* Wiki tab */}
        <button
          onClick={() => {
            setShowWiki(true);
            setShowProgress(false);
            setShowCompare(false);
            setShowHrZones(false);
          }}
          style={{
            flex: "0 0 auto",
            padding: "10px 16px",
            border: "none",
            borderBottom: showWiki ? "3px solid #16A085" : "3px solid transparent",
            background: "transparent",
            color: showWiki ? "#fff" : "rgba(255,255,255,0.45)",
            fontFamily: "'Georgia', serif",
            cursor: "pointer",
            textAlign: "center",
            minWidth: 80,
            transition: "all 0.15s",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: "bold", color: showWiki ? "#16A085" : "rgba(255,255,255,0.4)" }}>
            Wiki
          </div>
          <div style={{ fontSize: 11, marginTop: 1 }}>Glossary</div>
          <div style={{ fontSize: 10, opacity: 0.6, marginTop: 1 }}>📖</div>
        </button>

        {/* Dark mode toggle */}
        <button
          onClick={toggleDarkMode}
          title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          style={{
            flex: "0 0 auto",
            padding: "10px 14px",
            border: "none",
            borderBottom: "3px solid transparent",
            background: "transparent",
            color: "rgba(255,255,255,0.45)",
            fontFamily: "'Georgia', serif",
            cursor: "pointer",
            textAlign: "center",
            minWidth: 50,
            transition: "all 0.15s",
          }}
        >
          <div style={{ fontSize: 16 }}>{darkMode ? "☀️" : "🌙"}</div>
          <div style={{ fontSize: 9, marginTop: 1, letterSpacing: 0.5, color: "rgba(255,255,255,0.25)" }}>
            {darkMode ? "LIGHT" : "DARK"}
          </div>
        </button>

        {/* Settings / account button */}
        <button
          onClick={() => setShowSettings(s => !s)}
          style={{
            flex: "0 0 auto",
            padding: "10px 14px",
            border: "none",
            borderBottom: showSettings ? "3px solid #FC4C02" : "3px solid transparent",
            background: "transparent",
            color: showSettings ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.25)",
            fontFamily: "'Georgia', serif",
            cursor: "pointer",
            textAlign: "center",
            minWidth: 50,
            transition: "all 0.15s",
            marginLeft: "auto",
          }}
        >
          <div style={{ fontSize: 15 }}>⚙</div>
          <div style={{ fontSize: 9, marginTop: 1, letterSpacing: 0.5 }}>ACCOUNT</div>
        </button>
      </div>

      {/* Settings dropdown — rendered outside the nav to avoid overflow clipping */}
      {showSettings && (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 99 }}
            onClick={() => setShowSettings(false)}
          />
          <div
            style={{
              position: "fixed",
              top: 56,
              right: 8,
              zIndex: 101,
              background: "#1A1A2E",
              border: "1px solid #2A2A4E",
              borderRadius: 10,
              padding: "16px",
              minWidth: 220,
              boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
              fontFamily: "'Georgia', serif",
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: "rgba(255,255,255,0.35)",
                letterSpacing: 1.5,
                marginBottom: 10,
                fontFamily: "monospace",
              }}
            >
              TIME FORMAT
            </div>
            <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
              {[
                { key: "duration", label: "1:05:23" },
                { key: "minutes", label: "65 min" },
              ].map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setAndSaveTimeFormat(opt.key)}
                  style={{
                    flex: 1,
                    padding: "8px 4px",
                    border: "1px solid",
                    borderColor: timeFormat === opt.key ? "#FC4C02" : "#2A2A4E",
                    borderRadius: 6,
                    background: timeFormat === opt.key ? "#FC4C02" : "transparent",
                    color: timeFormat === opt.key ? "#fff" : "rgba(255,255,255,0.5)",
                    fontFamily: "monospace",
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div style={{ borderTop: "1px solid #2A2A4E", paddingTop: 12 }}>
              <button
                onClick={handleLogout}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "none",
                  background: "transparent",
                  color: "rgba(255,255,255,0.4)",
                  fontFamily: "'Georgia', serif",
                  fontSize: 13,
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                ⎋ Sign out
              </button>
            </div>
          </div>
        </>
      )}

      {showWiki ? (
        <Wiki />
      ) : showHrZones ? (
        <HrZones selectedZoneConfigId={selectedZoneConfigId} onSelectZoneConfig={selectZoneConfig} />
      ) : showProgress && showCompare ? (
        <Compare stravaId={stravaId} allRuns={allRuns} zoneConfig={zoneConfigs.find(c => c.id === (selectedZoneConfigId ?? zoneConfigs[0]?.id)) ?? null} />
      ) : showProgress ? (
        <Progress stravaId={stravaId} authToken={authToken} onRunsLoaded={setAllRuns} timeFormat={timeFormat} zoneConfigId={selectedZoneConfigId} />
      ) : (
        <PhaseView phase={current.data} zoneConfig={zoneConfigs.find(c => c.id === (selectedZoneConfigId ?? zoneConfigs[0]?.id)) ?? null} />
      )}
    </div>
  );
}
